import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { BareInput } from "components/DesignSystem/TextInput/TextInput";
import Modal from "components/DesignSystem/Modal/Modal";
import Loader from "components/DesignSystem/Loader/loader";
import { Cross } from "components/icons/Cross";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import {
  useGetEntityBanksQuery,
  useRequestAccountantAccessMutation,
} from "store/apis/bankConnections";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";

type Props = ModalProps & {
  groupId: string;
  entityId: string;
  entityName: string;
  channelId: string;
  onSuccess?: () => void;
};

export const RequestAccountantAccess = ({
  isOpen,
  close,
  groupId,
  entityId,
  entityName,
  channelId,
  onSuccess,
}: Props) => {
  const { alertToast, successToast } = useToast();
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [customBankNames, setCustomBankNames] = useState<string[]>([]);
  const [requestAccountantAccess, { isLoading }] =
    useRequestAccountantAccessMutation();

  const { data: ledgerBanks, isLoading: isFetching } = useGetEntityBanksQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );

  const bankItems = ledgerBanks?.items ?? [];

  const handleToggle = (entityItemId: string) => {
    setSelectedItemIds((prev) =>
      prev.includes(entityItemId)
        ? prev.filter((id) => id !== entityItemId)
        : [...prev, entityItemId]
    );
  };

  const handleAddMore = () => setCustomBankNames((prev) => [...prev, ""]);

  const handleCustomNameChange = (index: number, value: string) => {
    setCustomBankNames((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleRemoveCustom = (index: number) => {
    setCustomBankNames((prev) => prev.filter((_, i) => i !== index));
  };

  const validCustomNames = customBankNames.filter((n) => n.trim().length > 0);
  const canSubmit = selectedItemIds.length > 0 || validCustomNames.length > 0;

  const handleSendRequest = async () => {
    try {
      await requestAccountantAccess({
        entityId,
        entityItemIds: selectedItemIds,
        channelId,
        customBankNames: validCustomNames,
      }).unwrap();

      successToast({ message: "Accountant access requested successfully" });
      close();
      onSuccess?.();
    } catch (e) {
      alertToast({ message: (e as BackendError).data?.error?.message });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Request accountant access</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <div className="t-text-body-sm t-text-text-30 t-mb-4">
            Select the accounts to request access for:
          </div>
          {isFetching ? <Loader /> : null}
          <div className="t-flex t-flex-col t-gap-4">
            {!isFetching &&
              bankItems.map((item) => (
                <label
                  key={item.uuid}
                  className="t-flex t-items-center t-gap-3 t-cursor-pointer"
                >
                  <Checkbox
                    checked={selectedItemIds.includes(item.uuid)}
                    onChange={() => handleToggle(item.uuid)}
                  />
                  <div>
                    <div className="t-text-body-sm t-text-text-60">
                      {item.bank_brand?.name}
                    </div>
                    <div className="t-text-body-sm-lite t-text-text-30">
                      {entityName}
                    </div>
                  </div>
                </label>
              ))}
            {customBankNames.length > 0 && (
              <p className="t-text-body-sm t-text-text-30 t-m-0">
                Other data sources
              </p>
            )}
            {customBankNames.map((name, index) => (
              <div key={index} className="t-flex t-items-center t-gap-2">
                <Checkbox checked disabled onChange={() => {}} />
                <div className="t-flex-1">
                  <BareInput
                    value={name}
                    onChange={(e) =>
                      handleCustomNameChange(index, e.target.value)
                    }
                    placeholder="Enter data source name"
                    autoFocus={index === customBankNames.length - 1}
                  />
                  <p className="t-text-body-sm-lite t-text-text-30 t-m-0 t-mt-0.5 t-pl-3">
                    {entityName}
                  </p>
                </div>
                <Button
                  customType="ghost_icon"
                  size="small"
                  onClick={() => handleRemoveCustom(index)}
                >
                  <Cross />
                </Button>
              </div>
            ))}
            <Button
              customType="ghost"
              size="small"
              onClick={handleAddMore}
              disabled={isFetching}
            >
              + Add more
            </Button>
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Modal.RawClose asChild>
            <Button type="button">Cancel</Button>
          </Modal.RawClose>
          <Button
            customType="primary"
            onClick={handleSendRequest}
            disabled={!canSubmit || isLoading}
            isLoading={isLoading}
          >
            Send request
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
